// =============================================================================
// hooks/sales/useCampaignQueue.ts — Mutações da fila de operação de campanha
// (`campaign_members`, F35-S05/F35-S10, doc 26 §6.8/§8/§9).
//
// Endpoints (apps/api/src/modules/sales/campaigns/queue/routes.ts):
//   POST /api/sales/campaigns/:id/queue/claim-next          — reserva atômica
//   POST /api/sales/campaigns/:id/queue/:memberId/release   — devolve à fila
//   POST /api/sales/campaigns/:id/queue/:memberId/resolve   — encerra com desfecho (F35-S15)
//   POST /api/sales/campaigns/:id/queue/:memberId/heartbeat — renova o lease
//
// Sem `useQuery` aqui de propósito: `claim-next` já É a leitura (reserva
// atômica com `SELECT ... FOR UPDATE SKIP LOCKED` no servidor) — cachear o
// resultado do lado do cliente não faz sentido para um recurso que muda de
// dono a cada chamada. `features/sales/campaigns/operate/useQueueSession.ts`
// é quem orquestra o ciclo de vida (claim → heartbeat → release) em cima
// destas três mutações puras.
//
// Lease (doc 26 §8): a duração REAL é decidida no servidor
// (`DEFAULT_LEASE_SECONDS`, apps/api/.../queue/service.ts — 300s hoje).
// `QUEUE_LEASE_SECONDS` aqui é só o valor usado para o countdown VISUAL
// inicial antes da 1ª resposta do servidor confirmar `claimExpiresAt` — o
// componente sempre re-sincroniza com a data absoluta que a API devolve,
// nunca conta a partir deste número sozinho.
// =============================================================================

import type {
  CampaignQueueClaimResponse,
  CampaignQueueHeartbeatResponse,
  CampaignQueueLeadContext,
  CampaignQueueMember,
  CampaignQueueReleaseResponse,
  CampaignQueueResolveResponse,
  SalesOutcomeCode,
} from '@elemento/shared-schemas';
import {
  CampaignQueueClaimResponseSchema,
  CampaignQueueHeartbeatResponseSchema,
  CampaignQueueReleaseResponseSchema,
  CampaignQueueResolveResponseSchema,
} from '@elemento/shared-schemas';
import { useMutation } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api';

export type {
  CampaignQueueClaimResponse,
  CampaignQueueHeartbeatResponse,
  CampaignQueueLeadContext,
  CampaignQueueMember,
  CampaignQueueReleaseResponse,
  CampaignQueueResolveResponse,
};

/** Espelha `DEFAULT_LEASE_SECONDS` do servidor — só para o 1º render do countdown. */
export const QUEUE_LEASE_SECONDS = 300;

/** Intervalo de heartbeat — bem abaixo do lease (300s) para nunca perder a janela por causa do próprio delay de rede. */
export const QUEUE_HEARTBEAT_INTERVAL_MS = 90_000;

/** Código retornado pelo servidor quando o lease já foi perdido (expirado, liberado ou de outro usuário). */
export const LEASE_LOST_ERROR_CODE = 'CAMPAIGN_QUEUE_LEASE_LOST';

/** `true` quando o erro representa perda de posse do lease — sinal para a tela buscar um novo item, nunca reenviar o mesmo. */
export function isLeaseLostError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

// ---------------------------------------------------------------------------
// Claim — POST /api/sales/campaigns/:id/queue/claim-next
// 204 (sem corpo) = fila vazia — traduzido para `null`, nunca um erro.
// ---------------------------------------------------------------------------

async function claimNextQueue(campaignId: string): Promise<CampaignQueueClaimResponse | null> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(campaignId)}/queue/claim-next`,
    {},
  );
  if (raw === undefined) return null; // 204 — apiFetch traduz No Content para undefined
  return CampaignQueueClaimResponseSchema.parse(raw);
}

export function useClaimNextQueue(): {
  claimAsync: (campaignId: string) => Promise<CampaignQueueClaimResponse | null>;
  isPending: boolean;
} {
  const mutation = useMutation({ mutationFn: claimNextQueue });
  return {
    claimAsync: (campaignId) => mutation.mutateAsync(campaignId),
    isPending: mutation.isPending,
  };
}

// ---------------------------------------------------------------------------
// Release — POST /api/sales/campaigns/:id/queue/:memberId/release
// ---------------------------------------------------------------------------

export interface ReleaseQueueMemberInput {
  campaignId: string;
  memberId: string;
  reason?: string | undefined;
  /** `true` no `pagehide`/unmount — usa `keepalive` para sobreviver ao fechamento da aba (best-effort, doc 26 §6.8). */
  keepalive?: boolean;
}

async function releaseQueueMember(
  input: ReleaseQueueMemberInput,
): Promise<CampaignQueueReleaseResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(input.campaignId)}/queue/${encodeURIComponent(input.memberId)}/release`,
    input.reason !== undefined ? { reason: input.reason } : {},
    input.keepalive ? { keepalive: true } : undefined,
  );
  return CampaignQueueReleaseResponseSchema.parse(raw);
}

export function useReleaseQueueMember(): {
  releaseAsync: (input: ReleaseQueueMemberInput) => Promise<CampaignQueueReleaseResponse>;
  isPending: boolean;
} {
  const mutation = useMutation({ mutationFn: releaseQueueMember });
  return { releaseAsync: (input) => mutation.mutateAsync(input), isPending: mutation.isPending };
}

/**
 * Variante fire-and-forget para `pagehide`/cleanup de desmontagem — nunca
 * aguardada (a aba pode já estar fechando). Erros são engolidos de
 * propósito: a expiração do lease no servidor cobre o resto (doc 26 §6.8,
 * "Fechar a tela libera o item — best-effort no unload; a expiração cobre o
 * resto").
 */
export function releaseQueueMemberBestEffort(campaignId: string, memberId: string): void {
  void releaseQueueMember({ campaignId, memberId, reason: undefined, keepalive: true }).catch(
    () => undefined,
  );
}

// ---------------------------------------------------------------------------
// Resolve — POST /api/sales/campaigns/:id/queue/:memberId/resolve
//
// Encerramento REAL do membro (F35-S15) — leva para `completed` (outcomes
// terminais, ex.: `converted`/`not_interested`/`do_not_contact`) ou
// `waiting_callback` (outcomes com `requiresAppointment`, ex.:
// `callback_requested`, com `nextActionAt`). Diferente de `release`, que
// sempre devolve para `available` incondicionalmente — os dois caminhos da
// tela de operação não são intercambiáveis (F35-S18, doc 26 §12).
// ---------------------------------------------------------------------------

export interface ResolveQueueMemberInput {
  campaignId: string;
  memberId: string;
  outcomeCode: SalesOutcomeCode;
  /** Obrigatório no servidor quando o outcome exige retorno agendado (422 caso contrário). */
  nextActionAt?: string | undefined;
  reason?: string | undefined;
}

async function resolveQueueMember(
  input: ResolveQueueMemberInput,
): Promise<CampaignQueueResolveResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(input.campaignId)}/queue/${encodeURIComponent(input.memberId)}/resolve`,
    {
      outcomeCode: input.outcomeCode,
      ...(input.nextActionAt !== undefined ? { nextActionAt: input.nextActionAt } : {}),
      ...(input.reason !== undefined ? { reason: input.reason } : {}),
    },
  );
  return CampaignQueueResolveResponseSchema.parse(raw);
}

export function useResolveQueueMember(): {
  resolveAsync: (input: ResolveQueueMemberInput) => Promise<CampaignQueueResolveResponse>;
  isPending: boolean;
} {
  const mutation = useMutation({ mutationFn: resolveQueueMember });
  return { resolveAsync: (input) => mutation.mutateAsync(input), isPending: mutation.isPending };
}

// ---------------------------------------------------------------------------
// Heartbeat — POST /api/sales/campaigns/:id/queue/:memberId/heartbeat
// ---------------------------------------------------------------------------

async function heartbeatQueueMember(
  campaignId: string,
  memberId: string,
): Promise<CampaignQueueHeartbeatResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(campaignId)}/queue/${encodeURIComponent(memberId)}/heartbeat`,
    {},
  );
  return CampaignQueueHeartbeatResponseSchema.parse(raw);
}

export function useHeartbeatQueueMember(): {
  heartbeatAsync: (campaignId: string, memberId: string) => Promise<CampaignQueueHeartbeatResponse>;
} {
  const mutation = useMutation({
    mutationFn: ({ campaignId, memberId }: { campaignId: string; memberId: string }) =>
      heartbeatQueueMember(campaignId, memberId),
  });
  return {
    heartbeatAsync: (campaignId, memberId) => mutation.mutateAsync({ campaignId, memberId }),
  };
}
